import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Check, EyeOff, Lock, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Poll } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { Button, ConfirmDialog, Input, cn, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';

/**
 * Het peilingblok onder een bericht (Stap 26).
 *
 * Twee dingen staan hier bovenaan en niet onderaan, en dat is de hele vormgeving van dit blok:
 *
 * 1. **Anoniem of op naam.** Die zin staat vóór de opties, niet in een voetnoot. Een medewerker die
 *    pas na het stemmen leest dat zijn werkgever zijn keuze ziet, heeft een keuze gemaakt die hij
 *    niet gemaakt had — en die niet terug te draaien is.
 * 2. **Dat een stem definitief is.** Ook dat staat erbij vóór het stemmen. Bij een anonieme peiling
 *    kán de server een stem niet terugnemen: hij weet niet welke teller hij zou moeten verlagen.
 *
 * De uitslag verschijnt als staven en niet als een cirkeldiagram: op 390 pixels zijn vier staven
 * met een getal leesbaar en vier cirkelsegmenten niet.
 */
export function PollBlock({ postId }: { postId: string }) {
  const { api } = useSession();

  const poll = useQuery({
    queryKey: ['poll', postId],
    queryFn: async (): Promise<Poll | null> => {
      const { data, error } = await api.GET('/api/v1/feed/polls', {
        params: { query: { postIds: postId } },
      });
      // Een mislukte peiling mag het bericht niet omver halen: lezen is waar deze app voor is.
      if (error || !data) return null;
      return ((data as { items: Poll[] }).items[0] ?? null) as Poll | null;
    },
  });

  if (!poll.data) return null;
  return <PollCard poll={poll.data} postId={postId} />;
}

function PollCard({ poll, postId }: { poll: Poll; postId: string }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<string[]>([]);

  const [bevestigen, setBevestigen] = useState(false);
  const [eigenOptie, setEigenOptie] = useState('');

  /** Een eigen optie toevoegen (Extra 6, punt 5). */
  const addOption = useMutation({
    mutationFn: async (label: string) => {
      const { error } = await api.POST('/api/v1/feed/polls/{pollId}/options', {
        params: { path: { pollId: poll.id } },
        body: { label },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setEigenOptie('');
      void queryClient.invalidateQueries({ queryKey: ['poll', poll.postId] });
      void queryClient.invalidateQueries({ queryKey: ['polls'] });
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const vote = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/feed/polls/{pollId}/vote', {
        params: { path: { pollId: poll.id } },
        body: { optionIds: chosen },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      // Geen optimistische update: een stem is definitief, en dan hoort de medewerker te zien dat
      // de server hem ook echt heeft vastgelegd.
      void queryClient.invalidateQueries({ queryKey: ['poll', postId] });
      toast.show({ tone: 'success', title: t('poll.voted') });
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('poll.voteFailed') }),
  });

  const toggle = (optionId: string) => {
    setChosen((current) => {
      if (poll.multi) {
        return current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
      }
      return [optionId];
    });
  };

  const total = poll.options.reduce((sum, option) => sum + (option.voteCount ?? 0), 0);
  const canVote = !poll.votedByMe && !poll.closed;

  return (
    <section
      data-testid="poll"
      className="mt-element rounded-lg border border-border-light bg-surface-sunk p-element"
    >
      <div className="flex items-start justify-between gap-element">
        <h3 className="font-heading text-title-sm font-semibold text-text-primary">
          {poll.question}
        </h3>
        {poll.closed && (
          <span className="inline-flex shrink-0 items-center gap-1 text-small text-text-muted">
            <Lock aria-hidden className="h-3.5 w-3.5" />
            {t('poll.closed')}
          </span>
        )}
      </div>

      {/* De modus, vóór de opties. Niet als voetnoot en niet als pictogram alleen: dit is de ene
          zin die bepaalt of iemand eerlijk antwoordt. */}
      <p
        data-testid="poll-mode"
        className={cn(
          'mt-1 inline-flex items-center gap-1.5 text-small font-semibold',
          poll.mode === 'anonymous' ? 'text-brand-primary' : 'text-text-secondary',
        )}
      >
        {poll.mode === 'anonymous' ? (
          <EyeOff aria-hidden className="h-4 w-4 shrink-0" />
        ) : (
          <Users aria-hidden className="h-4 w-4 shrink-0" />
        )}
        {poll.mode === 'anonymous' ? t('poll.isAnonymous') : t('poll.isNamed')}
      </p>

      {canVote && <p className="mt-1 text-small text-text-muted">{t('poll.voteIsFinal')}</p>}

      <ul className="mt-element flex flex-col gap-2">
        {poll.options.map((option) => {
          const count = option.voteCount;
          const share = count !== null && total > 0 ? Math.round((count / total) * 100) : 0;
          const picked = chosen.includes(option.id);
          const mine = option.chosenByMe === true;

          if (canVote) {
            return (
              <li key={option.id}>
                <button
                  type="button"
                  data-touch-target
                  data-testid="poll-option"
                  aria-pressed={picked}
                  onClick={() => toggle(option.id)}
                  className={cn(
                    'flex min-h-touch w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-content transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary',
                    picked
                      ? 'border-brand-primary bg-surface text-text-primary'
                      : 'border-border-light bg-surface text-text-secondary hover:border-brand-primary',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center border',
                      poll.multi ? 'rounded-sm' : 'rounded-full',
                      picked ? 'border-brand-primary bg-brand-primary' : 'border-border',
                    )}
                  >
                    {picked && <Check className="h-3.5 w-3.5 text-text-on-dark" />}
                  </span>
                  {option.label}
                </button>
              </li>
            );
          }

          return (
            <li key={option.id} data-testid="poll-result" className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-element text-content">
                <span className="text-text-primary">
                  {option.label}
                  {mine && (
                    <span className="ml-1.5 text-small text-brand-primary">{t('poll.yours')}</span>
                  )}
                </span>
                {count !== null && (
                  <span className="shrink-0 font-semibold tabular-nums text-text-primary">
                    {count} · {share}%
                  </span>
                )}
              </div>
              {/* Alleen een staaf als er een getal bij hoort. Een staaf van nul breedte bij een
                  verborgen uitslag zou "niemand koos dit" betekenen, en dat is niet wat er staat. */}
              {count !== null && (
                <div className="h-1.5 w-full overflow-hidden rounded-pill bg-surface">
                  <div
                    className="h-full rounded-pill bg-brand-primary"
                    style={{ width: `${share}%` }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/*
        Zelf een optie toevoegen, als de opsteller dat aanzette (Extra 6, punt 5).

        Boven de stemknop en niet erna: wie een eigen antwoord toevoegt, wil het daarna ook
        kunnen aanvinken. De optie verschijnt onderaan de lijst, achter die van de opsteller.
      */}
      {poll.allowCustom && !poll.closed && !poll.votedByMe ? (
        <form
          className="mt-element flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (eigenOptie.trim().length > 0) addOption.mutate(eigenOptie.trim());
          }}
        >
          <div className="flex-1">
            <Input
              label={t('poll.addOption')}
              value={eigenOptie}
              maxLength={120}
              data-testid="poll-custom-option"
              onChange={(event) => setEigenOptie(event.target.value)}
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            loading={addOption.isPending}
            disabled={eigenOptie.trim().length === 0}
            data-testid="poll-custom-add"
          >
            {t('poll.add')}
          </Button>
        </form>
      ) : null}

      {canVote ? (
        <Button
          className="mt-element"
          data-testid="poll-vote"
          loading={vote.isPending}
          disabled={chosen.length === 0}
          onClick={() => (poll.mode === 'anonymous' ? setBevestigen(true) : vote.mutate())}
        >
          {t('poll.vote')}
        </Button>
      ) : (
        <p className="mt-element flex items-center gap-1.5 text-small text-text-muted">
          <BarChart3 aria-hidden className="h-4 w-4 shrink-0" />
          {poll.resultsVisible
            ? t('poll.voterCount', { count: poll.voterCount })
            : t(`poll.withheld.${poll.resultsWithheldReason ?? 'never'}`)}
        </p>
      )}
      {/*
        Een anonieme stem is definitief, en de server kan hem niet terugnemen (Extra 6, punt 5).

        Hij weet niet welke teller hij zou moeten verlagen - dat is juist wat anoniem betekent.
        Daarom staat er bij die soort een tussenstap; bij een peiling met namen niet, want daar
        kan de medewerker zijn keuze nog laten wijzigen door wie de peiling beheert.
      */}
      <ConfirmDialog
        open={bevestigen}
        title={t('poll.confirmTitle')}
        description={t('poll.confirmBody')}
        confirmLabel={t('poll.vote')}
        cancelLabel={t('common.cancel')}
        onOpenChange={setBevestigen}
        testId="poll-confirm"
        onConfirm={() => {
          setBevestigen(false);
          vote.mutate();
        }}
      />
    </section>
  );
}
